import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ContractTimeline from './ContractTimeline';
import { type StatusStep } from '../services/projectStatusUtils';
import { ProjectWorkflowStatusBadge } from './ProjectWorkflowStatusBadge';
import { payoutPresentationMilestones } from '../utils/payoutAvailability';
import { projectWorkflowStatus } from '../utils/milestoneWorkflowStatus';
import {
  MilestoneContractActions,
  MilestoneNodeActions,
  contractMilestoneActionState,
  type MilestoneActionHandlers
} from './MilestoneTimelineActions';
import type { Contract, ContractReplacement, Milestone, ProjectFundingStatus } from '../types';
import { timelineContracts } from '../utils/timelineContracts';
import { contractFrameTotals } from '../utils/contractFrameTotals';
import { contractFundingConfirmed } from '../utils/timelineFunding';
import { contractApi } from '../services/api';
import { StageVariationBadge, stageVariationState } from './StageVariationNotice';
import { lastActivityTime } from '../utils/lastActivity';
import { useMilestoneStarts } from '../hooks/useMilestoneStarts';
import { isCompletedProject } from '../utils/projectListSort';

const isPayableContract = (contract: { draftStatus?: string }) =>
  contract.draftStatus === 'signed_by_both';

// Same format as the project page's contract frame, so both show identical figures.
const formatFrameCurrency = (amount: number): string =>
  new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);

interface ProjectTimelineWidgetProps {
  /** The role's projects (already scoped by the caller / backend). */
  projects: any[];
  /** All contracts across those projects (each carries projectId). */
  contracts: Contract[];
  /** All milestones across those contracts (each carries contractId). */
  milestones: Milestone[];
  /** Status-instance steps per project id, for the shared status presentation. */
  statusSteps: Record<string, StatusStep[]>;
  /** Builds the primary button's target for a project (role-specific route). */
  projectHref: (project: any) => string;
  /**
   * Primary button label. Defaults to "Gå till Projekt". Pass an i18next-resolved
   * string (never a hardcoded one) to repurpose the widget — e.g. "Extern hjälp"
   * on the Extern hjälp → Mina Projekt view, where `projectHref` also changes to
   * open the project's Extern hjälp tab instead of its overview.
   */
  primaryActionLabel?: string;
  /** Empty-state text when there are no projects. Defaults to "Inga projekt än". */
  emptyStateText?: string;
  /** Company side: opens a contract the customer sent back in its editor. */
  onReviseContract?: (contract: Contract) => void;
  /** Heading instead of "Mina Projekt", e.g. "Mina ärenden" for a lawyer. */
  title?: string;
  /** Which party may receive the shared attestation action. Omit for read-only providers. */
  attestationSide?: 'company' | 'person';
  /**
   * The server's funding status per project id (as in the project view). When
   * present it decides whether stages can be attested; without it the widget
   * falls back to comparing paid funds with the contracts' amounts after ROT.
   */
  fundingByProject?: Record<string, ProjectFundingStatus>;
  /**
   * A contract's stages after an action in the timeline (e.g. a stage marked as
   * started), so the page updates them in place. With it, the timeline offers
   * "Markera som startad" just like the project page.
   */
  onMilestonesChanged?: (contractId: string, milestones: Milestone[]) => void;
  /** Card wrapper class; matches each home page's widget spacing. */
  className?: string;
  /**
   * Show the builder/company name as a badge on each project. Providers
   * (jurist/besiktningsman) work across companies and need this; the company's
   * own home does not (its projects are all its own), so it stays off there to
   * keep the company widget unchanged.
   */
  showCompanyBadge?: boolean;
}

/**
 * Shared "Tidslinje Projekt" start-page widget. Renders each contract in the
 * same contract frame as the project page (header, key figures, combined
 * timeline, agreement history; closed contracts folded) for every project,
 * sorted by most recent activity, with a latest-event banner. The to-dos
 * themselves live in the start page's "Att göra" list, not in the frame; the
 * stages waiting for the viewer only pulse here. Reused by the provider
 * (jurist / besiktningsman) home pages so their overview matches.
 */
export function ProjectTimelineWidget({
  projects: allProjects,
  contracts,
  milestones,
  statusSteps,
  projectHref,
  primaryActionLabel,
  emptyStateText,
  onReviseContract,
  title,
  attestationSide,
  fundingByProject,
  onMilestonesChanged,
  className = 'card mt-4',
  showCompanyBadge = false
}: ProjectTimelineWidgetProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Completed projects live under Projekt → Avslutade, not here (ClickUp 869feufxn).
  const projects = useMemo(() => allProjects.filter(project => !isCompletedProject(project)), [allProjects]);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const isSingleProject = projects.length === 1;
  // The same start action and conditions as the project page (useMilestoneStarts).
  const milestonesByContract = useMemo(() => milestones.reduce<Record<string, Milestone[]>>((byContract, milestone) => {
    (byContract[milestone.contractId] ??= []).push(milestone);
    return byContract;
  }, {}), [milestones]);
  const { timelineStartProps, startModal } = useMilestoneStarts({
    side: attestationSide ?? 'company',
    contracts,
    milestonesByContract,
    onSaved: (contract, updated) => onMilestonesChanged?.(contract.id, updated)
  });
  const canStart = Boolean(attestationSide && onMilestonesChanged);
  // ÄTA settlements, for the frame's "ÄTA #n gäller" badge. Fetched only for
  // ÄTA contracts in projects that are open, once each, without blocking the
  // page — until one arrives the frame shows "inkl. ÄTA #n" instead.
  const [settlements, setSettlements] = useState<Record<string, ContractReplacement | null>>({});
  const openAmendmentIds = useMemo(() => projects
    .filter(project => isSingleProject || expandedProjects.has(project.id))
    .flatMap(project => timelineContracts(contracts.filter(contract => contract.projectId === project.id)))
    .filter(contract => contract.stageVariation)
    .map(contract => contract.id), [projects, contracts, expandedProjects, isSingleProject]);
  useEffect(() => {
    const missing = openAmendmentIds.filter(id => !(id in settlements));
    if (!missing.length) return;
    setSettlements(previous => ({ ...previous, ...Object.fromEntries(missing.map(id => [id, null])) }));
    missing.forEach(id => {
      contractApi.getReplacement(id)
        .then(settlement => setSettlements(previous => ({ ...previous, [id]: settlement })))
        .catch(() => undefined);
    });
  }, [openAmendmentIds, settlements]);

  const toggleProject = (id: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className={className}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">
          <i className="feather-git-branch me-2"></i>
          {title ?? t('home.projectTimeline.title', 'Mina Projekt')}
        </h5>
        {projects.length > 0 && (
          <span className="badge bg-primary">
            {t('home.projectTimeline.projectCount', { count: projects.length })}
          </span>
        )}
      </div>
      <div className={`card-body${projects.length > 0 ? ' home-projects-body' : ''}`}>
        {projects.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="feather-inbox fs-1 d-block mb-3"></i>
            <p className="mb-0">
              {allProjects.length > 0
                ? t('home.projectTimeline.noOngoingProjects')
                : emptyStateText ?? t('home.projectTimeline.noProjects', 'Inga projekt än')}
            </p>
          </div>
        ) : (
          <div className="home-project-list">
            {(() => {
              // Sort projects with the most recent activity on top: the API's
              // "Senast uppdaterad", the same time the project lists sort by.
              const latestTs = (proj: any): number => {
                if (proj.lastActivityAt) return lastActivityTime(proj.lastActivityAt);
                const pcs = contracts.filter((c: any) => c.projectId === proj.id);
                const stamps: string[] = [];
                for (const c of pcs as any[]) {
                  if (c.draftSentAt) stamps.push(c.draftSentAt);
                  if (c.draftRespondedAt) stamps.push(c.draftRespondedAt);
                  if (c.companySignedAt) stamps.push(c.companySignedAt);
                  if (c.personSignedAt) stamps.push(c.personSignedAt);
                  if (c.updatedAt) stamps.push(c.updatedAt);
                }
                for (const m of milestones.filter((mm: any) =>
                  pcs.some((c: any) => c.id === mm.contractId)
                ) as any[]) {
                  if (m.updatedAt) stamps.push(m.updatedAt);
                }
                if (proj.updatedAt) stamps.push(proj.updatedAt);
                return stamps.reduce((acc: number, s: string) => Math.max(acc, new Date(s).getTime()), 0);
              };
              return [...projects].sort((a, b) => latestTs(b) - latestTs(a));
            })().map((project) => {
              const steps = statusSteps[project.id] || [];
              const projectContracts = timelineContracts(contracts.filter(c => c.projectId === project.id));
              const projectMilestones = milestones.filter((milestone: any) =>
                projectContracts.some((contract: any) => contract.id === milestone.contractId)
              );
              const displayStatus = projectWorkflowStatus(
                project.status,
                projectMilestones,
                project.workflowStatus
              );
              const payableProjectContracts = projectContracts.filter(isPayableContract);
              // The customer funds the amount after ROT ("Insatta medel" is
              // after ROT too). Comparing with the amount before ROT left every
              // ROT project "unfunded", so the cards never offered attestation.
              const projectFundingTotal = payableProjectContracts.reduce((sum: number, c: any) => {
                const amount = parseFloat(String(c.afterRotAmount ?? c.contractAmount ?? '0').replace(/[^0-9.-]/g, ''));
                return sum + (isNaN(amount) ? 0 : amount);
              }, 0);
              const projectFundingPaid = payableProjectContracts.reduce(
                (sum: number, c: any) => sum + (c.paidAmount || 0),
                0
              );
              const projectFunding = fundingByProject?.[project.id];
              const projectFunded = projectFunding
                ? projectFunding.isFunded
                : projectFundingTotal > 0 && projectFundingPaid >= projectFundingTotal;
              // The project view's actions, opened in the project view: the
              // attestation (BankID) and conflict modals live there.
              const openInProject = (milestone: Milestone, action: 'attest' | 'resolution') => {
                const [path, query] = projectHref(project).split('?');
                const params = new URLSearchParams(query);
                params.set('tab', 'timeline');
                params.set('milestoneId', milestone.id);
                params.set('action', action);
                navigate(`${path}?${params.toString()}`);
              };
              const actionHandlers: MilestoneActionHandlers = {
                onOpen: (milestone, mode) => openInProject(milestone, mode === 'resolution' ? 'resolution' : 'attest'),
                onAttestRemedied: milestone => openInProject(milestone, 'attest')
              };

              const isOpen = isSingleProject || expandedProjects.has(project.id);
              // Who the project is with (company for providers, else the customer)
              // and when it runs, as one grey line under the project's title.
              const counterpart = showCompanyBadge && project.companyName ? project.companyName : project.customerName;
              const period = project.startDate && project.endDate
                ? t('home.projectTimeline.period', { start: project.startDate, end: project.endDate })
                : project.startDate ? `${t('home.projectTimeline.start', 'Start')}: ${project.startDate}`
                  : project.endDate ? `${t('home.projectTimeline.end', 'Slut')}: ${project.endDate}` : null;
              const projectSubline = [counterpart, period].filter(Boolean).join(' · ');
              const panelId = `project-timeline-panel-${project.id}`;

              return (
                <div key={project.id} className="home-project">
                  <div className="home-project-header">
                    {(() => {
                      const heading = <span className="home-project-heading">
                        <span className="home-project-title project-card-title">
                          {t('home.projectTimeline.projectTitle', { number: project.projectNumber, description: project.description })}
                        </span>
                        {projectSubline && <span className="home-project-subline">{projectSubline}</span>}
                      </span>;
                      return isSingleProject ? heading : (
                        <button
                          type="button"
                          className="btn expandable-row-toggle home-project-toggle"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() => toggleProject(project.id)}
                        >
                          <i className={`feather-chevron-${isOpen ? 'up' : 'down'} home-project-chevron`} aria-hidden="true"></i>
                          {heading}
                        </button>
                      );
                    })()}
                    <span className="home-project-status">
                      <ProjectWorkflowStatusBadge
                        project={project}
                        milestones={payoutPresentationMilestones(projectMilestones, projectFunding?.milestones)}
                        steps={steps}
                      />
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-primary home-project-open"
                      onClick={() => navigate(projectHref(project))}
                    >
                      {primaryActionLabel ?? t('home.projectTimeline.goToProject', 'Gå till Projekt')}
                      <i className="feather-arrow-right ms-1" aria-hidden="true"></i>
                    </button>
                  </div>
                  <div id={panelId} hidden={!isSingleProject && !isOpen}>
                  <div className="home-project-body contract-frames">
                  {projectContracts.length === 0 ? (
                    <div className="text-muted small fst-italic">
                      {t('home.projectTimeline.noContracts', 'Inga kontrakt än')}
                    </div>
                  ) : (
                    <div className="d-flex flex-column">
                      {projectContracts.map((c: any) => {
                        const cMilestones = milestones.filter((m: any) => m.contractId === c.id);
                        const contractFunded = contractFundingConfirmed(c,
                          projectFunding?.contracts.find(item => item.contractId === c.id), projectFunded);
                        const parent = c.parentContractId
                          ? projectContracts.find((x: any) => x.id === c.parentContractId) || null
                          : null;
                        const [projectPath, projectQuery] = projectHref(project).split('?');
                        const openProjectWith = (params: Record<string, string>) => {
                          const query = new URLSearchParams(projectQuery);
                          Object.entries(params).forEach(([key, value]) => query.set(key, value));
                          navigate(`${projectPath}?${query.toString()}`);
                        };
                        const frameTotals = contractFrameTotals({
                          contract: c,
                          fundingContract: projectFunding?.contracts.find(item => item.contractId === c.id),
                          milestones: cMilestones,
                          relatedContracts: contracts,
                          relatedMilestones: milestones,
                          payoutOreByMilestone: Object.fromEntries((projectFunding?.milestones ?? [])
                            .map(milestone => [milestone.milestoneId, milestone.netPayoutOre])),
                          viewerRole: attestationSide,
                          t,
                          formatCurrency: formatFrameCurrency,
                          onPay: () => openProjectWith({ tab: 'timeline', paymentPicker: 'open', paymentContractId: c.id })
                        });
                        const actionMilestoneIds = attestationSide
                          ? contractMilestoneActionState(c, cMilestones, contractFunded, attestationSide)
                              .attestableMilestones.map(milestone => milestone.id)
                          : undefined;
                        return (
                          <div key={c.id}>
                            <ContractTimeline
                              contract={c}
                              milestones={cMilestones}
                              relatedContracts={contracts}
                              relatedMilestones={milestones}
                              parentContract={parent}
                              projectFunded={contractFunded}
                              viewerRole={attestationSide}
                              customerName={project.customerName}
                              onReviseContract={onReviseContract}
                              fundingMilestones={projectFunding?.milestones.filter(
                                milestone => milestone.contractId === c.id
                              )}
                              fundingContract={projectFunding?.contracts.find(contract => contract.contractId === c.id)}
                              relatedFundingContracts={projectFunding?.contracts}
                              actionMilestoneIds={actionMilestoneIds}
                              {...(canStart ? timelineStartProps(c, cMilestones) : {})}
                              renderMilestoneActions={attestationSide ? (milestone, closeDetails) => {
                                const candidateContract = contracts.find(item => item.id === milestone.contractId) ?? c;
                                return (
                                <MilestoneNodeActions
                                  milestone={milestone}
                                  contract={candidateContract}
                                  projectFunded={contractFundingConfirmed(candidateContract,
                                    projectFunding?.contracts.find(item => item.contractId === candidateContract.id), projectFunded)}
                                  side={attestationSide}
                                  handlers={actionHandlers}
                                  closeDetails={closeDetails}
                                />
                                );
                              } : undefined}
                              projectCompleted={project.status === 'completed'}
                              projectExternallyDisputed={
                                project.status === 'dispute' || displayStatus === 'external_dispute'
                              }
                              onSignContract={attestationSide ? target =>
                                openProjectWith({ tab: 'contract', contractId: target.id }) : undefined}
                              frameSlots={{
                                ...frameTotals,
                                timelineFirst: true,
                                // ÄTA is named as text on the header's second line ("inkl. ÄTA #3").
                                compactHeader: true,
                                // The stage actions sit in the frame, under the timeline,
                                // so they fold away with their contract.
                                afterTimeline: attestationSide ? (
                                  <div className="project-card-actions" data-testid={`project-card-actions-${c.id}`}>
                                    <MilestoneContractActions
                                      contract={c}
                                      milestones={cMilestones}
                                      projectFunded={contractFunded}
                                      side={attestationSide}
                                      handlers={actionHandlers}
                                      payoutOreByMilestone={Object.fromEntries((projectFunding?.milestones ?? [])
                                        .filter(milestone => milestone.contractId === c.id)
                                        .map(milestone => [milestone.milestoneId, milestone.netPayoutOre]))}
                                    />
                                  </div>
                                ) : undefined,
                                headerBadge: settlements[c.id]?.amendmentScope &&
                                  stageVariationState(c, settlements[c.id]!) === 'active'
                                  ? <StageVariationBadge contract={c} settlement={settlements[c.id]!} />
                                  : undefined
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {startModal}
    </div>
  );
}
